import { WorkTypeModel } from "../../../../factories/work-type/models/work-type.model";
import {
  AutomationActionModel,
  AutomationActionValue,
  AutomationConditionModel,
  AutomationConditionValue,
  AutomationSerializedValue,
  AutomationUserModel,
} from "../../../../slices/AutomationSlice";
import {
  AutomationItem,
  AutomationLookupOptions,
} from "../../../work/pages/automation/automation.constants";
import {
  parseAutomationActionValue,
  parseAutomationConditionValue,
  resolveOptionLabel,
} from "../../../work/pages/automation/automation.helpers";

export type WorkTypeDefaultAutomationModel = {
  id?: string;
  defaultAutomationId?: string;
  workTypeId?: string;
  orgId?: string;
  createdById?: string;
  isActive?: boolean;
  isExecuted?: boolean;
  isSuccess?: boolean;
  conditionId: string;
  actionId: string;
  conditionValue?: AutomationSerializedValue | AutomationConditionValue;
  noOfDays?: number | null;
  actionValue?: AutomationSerializedValue | AutomationActionValue;
  executedOn?: string | null;
  createdBy?: AutomationUserModel | null;
  condition?: AutomationConditionModel | null;
  action?: AutomationActionModel | null;
  createdAt?: string;
};

const getAutomationUserName = (user?: AutomationUserModel | null) => {
  const fullName = [
    user?.userProfile?.firstName,
    user?.userProfile?.middleName,
    user?.userProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user?.userProfile?.email || "";
};

const getSerializedAutomationValue = (
  value:
    | AutomationSerializedValue
    | AutomationConditionValue
    | AutomationActionValue
    | undefined,
): AutomationSerializedValue | undefined => {
  if (typeof value === "string" || value === null || value === undefined) {
    return value;
  }

  return JSON.stringify(value);
};

const getDefaultAutomations = (
  workType: WorkTypeModel,
): WorkTypeDefaultAutomationModel[] => {
  return workType?.defaultAutomation ?? [];
};

export const mapWorkTypeDefaultAutomationsToItems = ({
  workType,
  options,
  conditions,
}: {
  workType: WorkTypeModel;
  options: AutomationLookupOptions;
  conditions: AutomationConditionModel[];
}): AutomationItem[] => {
  const conditionById = new Map<string, AutomationConditionModel>();
  const actionById = new Map<string, AutomationActionModel>();

  conditions.forEach((condition) => {
    conditionById.set(condition.id, condition);
    condition.conditionAction.forEach(({ action }) => {
      actionById.set(action.id, action);
    });
  });

  const getCreatedByLabel = (createdById?: string) => {
    return resolveOptionLabel(options, "users", createdById, "Unknown user");
  };

  return getDefaultAutomations(workType)
    .map((automation) => {
      const id = automation.defaultAutomationId ?? automation.id;
      const condition =
        automation.condition ?? conditionById.get(automation.conditionId);
      const action = automation.action ?? actionById.get(automation.actionId);

      if (!id) {
        return null;
      }

      const item: AutomationItem = {
        id,
        scope: "work" as const,
        workItemId: automation.workTypeId ?? workType.id,
        conditionId: automation.conditionId,
        conditionKey: condition?.conditionKey,
        conditionValue: parseAutomationConditionValue(
          getSerializedAutomationValue(automation.conditionValue),
        ),
        actionId: automation.actionId,
        actionKey: action?.actionKey,
        actionValue: parseAutomationActionValue(
          getSerializedAutomationValue(automation.actionValue),
        ),
        createdBy:
          getAutomationUserName(automation.createdBy) ||
          getCreatedByLabel(automation.createdById),
        createdAt: automation.createdAt ?? new Date().toISOString(),
        lastRunAt: automation.executedOn ?? undefined,
        lastRunStatus: !automation.isExecuted
          ? "idle"
          : automation.isSuccess
          ? "success"
          : "failed",
      };

      return item;
    })
    .filter((item): item is AutomationItem => Boolean(item))
    .sort(
      (first, second) =>
        new Date(second.createdAt).getTime() -
        new Date(first.createdAt).getTime(),
    );
};
